// Customer sign-in, Cognito half.
//
// Customers sign in to the /account pages with their email and a one-time code
// (Cognito choice-based USER_AUTH with EMAIL_OTP). The pool is set up in
// helinhair-infrastructure (stacks/1.0.0/cognito_customer): there are no
// passwords and no sign-up - the webshop registers a buyer when they pay.
//
// These three calls are unauthenticated JSON POSTs to the Cognito API, so no AWS
// SDK is bundled. Feature code never imports this file: it goes through
// customerSession.ts, which owns the tokens.

export interface CustomerTokens {
  /** The ID token - the credential the customer APIs take (it carries `email`). */
  idToken: string
  refreshToken: string
  /** Epoch ms when the ID token expires. */
  expiresAt: number
}

/** A Cognito error, by its short type (`CodeMismatchException`, ...). */
export class CognitoError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'CognitoError'
    this.code = code
  }
}

interface PoolConfig {
  region: string
  clientId: string
}

/**
 * The pool from `VITE_CUSTOMER_POOL_ID` (its region is the part before `_`) and
 * `VITE_CUSTOMER_CLIENT_ID`. Null while either is missing: sign-in is then off.
 */
export function customerPoolConfig(): PoolConfig | null {
  const poolId = (import.meta.env.VITE_CUSTOMER_POOL_ID as string | undefined)?.trim() ?? ''
  const clientId = (import.meta.env.VITE_CUSTOMER_CLIENT_ID as string | undefined)?.trim() ?? ''
  const region = poolId.split('_')[0]
  if (!clientId || !poolId.includes('_') || !region) return null
  return { region, clientId }
}

function requireConfig(): PoolConfig {
  const config = customerPoolConfig()
  if (!config) throw new CognitoError('NotConfigured', 'Customer sign-in is not configured')
  return config
}

async function call<T>(target: string, body: Record<string, unknown>): Promise<T> {
  const { region, clientId } = requireConfig()
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify({ ClientId: clientId, ...body }),
  })
  const text = await res.text()
  const data = (text ? JSON.parse(text) : {}) as Record<string, unknown>
  if (!res.ok) {
    const type = String(data.__type ?? 'UnknownError')
    throw new CognitoError(type.split('#').pop() as string, String(data.message ?? `Sign-in failed (${res.status})`))
  }
  return data as T
}

interface AuthResult {
  IdToken: string
  RefreshToken?: string
  ExpiresIn: number
}

function toTokens(result: AuthResult, refreshToken: string): CustomerTokens {
  return {
    idToken: result.IdToken,
    refreshToken: result.RefreshToken ?? refreshToken,
    expiresAt: Date.now() + result.ExpiresIn * 1000,
  }
}

/**
 * Asks Cognito to email a sign-in code. Returns the session to answer it with.
 * An unknown email gets the same answer (the pool hides who is a customer), so
 * the caller always moves on to "we sent a code".
 */
export async function startEmailSignIn(email: string): Promise<string> {
  const res = await call<{ Session: string }>('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  })
  return res.Session
}

/** Answers the emailed code. A wrong code throws `CodeMismatchException`. */
export async function answerEmailCode(email: string, session: string, code: string): Promise<CustomerTokens> {
  const res = await call<{ AuthenticationResult?: AuthResult; Session?: string }>('RespondToAuthChallenge', {
    ChallengeName: 'EMAIL_OTP',
    Session: session,
    ChallengeResponses: { USERNAME: email, EMAIL_OTP_CODE: code },
  })
  if (!res.AuthenticationResult) {
    throw new CognitoError('ChallengeNotCompleted', 'Sign-in needs another step')
  }
  return toTokens(res.AuthenticationResult, '')
}

/** A fresh ID token from the refresh token (which Cognito keeps the same). */
export async function refreshTokens(refreshToken: string): Promise<CustomerTokens> {
  const res = await call<{ AuthenticationResult: AuthResult }>('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  })
  return toTokens(res.AuthenticationResult, refreshToken)
}

/** Ends the session server-side: the refresh token and its ID tokens stop working. */
export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  await call('RevokeToken', { Token: refreshToken })
}

/** The `email` claim of an ID token. Display only - the APIs verify the token themselves. */
export function emailOf(idToken: string): string | null {
  try {
    const payload = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(payload)) as { email?: unknown }
    return typeof claims.email === 'string' ? claims.email : null
  } catch {
    return null
  }
}
